import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Card, CardContent, Stack, Button } from '@mui/material';
import {
  Add,
  Note,
  Edit,
  Info,
  People,
  Person,
  FileUpload,
  FileDownload,
} from '@mui/icons-material';
import CollaboratorItem from '../../CollaboratorItem';
import useThemeStyles from '../../../../../hooks/useThemeStyles';

const ProjectSettingsModeBody = ({
  chapters,
  notes,
  collaborators,
  activeProject,
  handleAddChapter,
  handleAddCharacter,
  handleEditProject,
  handleExportProject,
  handleImportDocument,
  onSwitchMode,
  formatDate,
}) => {
  const { colors } = useThemeStyles();

  const actionButtonSx = {
    textTransform: 'none',
    color: colors.text.primary,
    borderColor: colors.border.subtle,
    justifyContent: 'flex-start',
    '&:hover': {
      backgroundColor: colors.sidebar.headerHover,
      borderColor: colors.interactive.primary,
    },
  };

  const quickActions = [
    { label: 'Add Chapter', icon: <Add />, onClick: handleAddChapter },
    {
      label: 'Add Note',
      icon: <Note />,
      onClick: onSwitchMode ? () => onSwitchMode('notes') : null,
    },
    { label: 'Add Character', icon: <Person />, onClick: handleAddCharacter },
    { label: 'Edit Project', icon: <Edit />, onClick: handleEditProject },
    { label: 'Import Document', icon: <FileUpload />, onClick: handleImportDocument },
    { label: 'Export Project', icon: <FileDownload />, onClick: handleExportProject },
  ].filter((action) => Boolean(action.onClick));

  const stats = [
    { label: 'Chapters', value: chapters?.length || 0 },
    { label: 'Notes', value: notes?.length || 0 },
    { label: 'Words', value: (activeProject?.wordCount || 0).toLocaleString() },
    { label: 'Collaborators', value: collaborators?.length || 0 },
  ];

  const cardSx = {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: `1px solid ${colors.border.subtle}`,
    width: '100%',
    minWidth: 0,
  };

  const cardTitleSx = {
    color: colors.text.primary,
    fontSize: '0.9375rem',
    fontWeight: 600,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        p: 2,
        backgroundColor: colors.sidebar.background,
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Stack spacing={3}>
        {/* Quick Actions */}
        <Card sx={cardSx}>
          <CardContent sx={{ width: '100%', minWidth: 0 }}>
            <Typography variant="h6" gutterBottom sx={cardTitleSx}>
              Quick Actions
            </Typography>
            <Stack spacing={1}>
              {quickActions.map((action) => (
                <Button
                  key={action.label}
                  variant="outlined"
                  fullWidth
                  startIcon={action.icon}
                  onClick={action.onClick}
                  sx={actionButtonSx}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Project Stats */}
        <Card sx={cardSx}>
          <CardContent sx={{ width: '100%', minWidth: 0 }}>
            <Typography variant="h6" gutterBottom sx={cardTitleSx}>
              Project Stats
            </Typography>
            <Stack spacing={2}>
              {stats.map((stat) => (
                <Box key={stat.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    {stat.label}:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    sx={{ color: colors.text.primary }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Project Details */}
        <Card sx={cardSx}>
          <CardContent sx={{ width: '100%', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Info sx={{ fontSize: 22, color: colors.interactive.primary }} />
              <Typography variant="h6" sx={cardTitleSx}>
                Project Details
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  Owner
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{
                    color: colors.text.primary,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {activeProject?.thinOwner?.name || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  Type
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.text.primary,
                    textTransform: 'capitalize',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {(activeProject?.type || 'Novel').replace(/_/g, ' ')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  Status
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.text.primary, textTransform: 'capitalize' }}
                >
                  {(activeProject?.status || 'draft').replace(/_/g, ' ')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  Created
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text.primary }}>
                  {activeProject?.createdAt && formatDate
                    ? formatDate(activeProject.createdAt)
                    : 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                  Last Updated
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text.primary }}>
                  {activeProject?.updatedAt && formatDate
                    ? formatDate(activeProject.updatedAt)
                    : 'Unknown'}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Collaborators */}
        <Card sx={cardSx}>
          <CardContent sx={{ width: '100%', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <People sx={{ fontSize: 22, color: colors.interactive.primary }} />
              <Typography variant="h6" sx={cardTitleSx}>
                Collaborators
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                ({collaborators?.length || 0})
              </Typography>
            </Box>
            {collaborators && collaborators.length > 0 ? (
              <Stack spacing={1.5}>
                {collaborators.map((collaborator, index) => (
                  <CollaboratorItem
                    key={collaborator.id || index}
                    collaborator={collaborator}
                    role={
                      activeProject?.thinCoauthors?.some(
                        (coauthor) => coauthor.id === collaborator.id
                      )
                        ? 'Co-author'
                        : 'Editor'
                    }
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                No collaborators yet
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

ProjectSettingsModeBody.propTypes = {
  chapters: PropTypes.arrayOf(PropTypes.shape({})),
  notes: PropTypes.arrayOf(PropTypes.shape({})),
  collaborators: PropTypes.arrayOf(PropTypes.shape({})),
  activeProject: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    wordCount: PropTypes.number,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    thinOwner: PropTypes.shape({ name: PropTypes.string, id: PropTypes.string }),
    thinCoauthors: PropTypes.arrayOf(PropTypes.shape({})),
    thinEditors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  handleAddChapter: PropTypes.func,
  handleAddCharacter: PropTypes.func,
  handleEditProject: PropTypes.func,
  handleExportProject: PropTypes.func,
  handleImportDocument: PropTypes.func,
  onSwitchMode: PropTypes.func,
  formatDate: PropTypes.func,
};

ProjectSettingsModeBody.defaultProps = {
  chapters: [],
  notes: [],
  collaborators: [],
  activeProject: null,
  handleAddChapter: null,
  handleAddCharacter: null,
  handleEditProject: null,
  handleExportProject: null,
  handleImportDocument: null,
  onSwitchMode: null,
  formatDate: null,
};

export default ProjectSettingsModeBody;
